import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Loader2, KeyRound } from "lucide-react";
import { changePassword } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { currentUser, refreshPermissions } = useAuth();
  const forced = !!currentUser?.must_change_password;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setStatus("error");
      setMessage("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus("error");
      setMessage("Passwords do not match.");
      return;
    }

    setStatus("loading");
    setMessage("");
    try {
      await changePassword(currentPassword, newPassword);
      await refreshPermissions();
      navigate("/", { replace: true });
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message || "Failed to change password.");
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-semibold">{forced ? "Set a new password" : "Change password"}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          {forced
            ? "Your password was set by an administrator. Please choose a new password to continue."
            : "Enter your current password and choose a new one."}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="currentPassword" className="text-sm">{forced ? "Current (temporary) password" : "Current password"}</Label>
            <PasswordInput
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={status === "loading"}
              required
              autoComplete="current-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="newPassword" className="text-sm">New password</Label>
            <PasswordInput
              id="newPassword"
              placeholder="At least 6 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={status === "loading"}
              minLength={6}
              required
              autoComplete="new-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword" className="text-sm">Confirm new password</Label>
            <PasswordInput
              id="confirmPassword"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={status === "loading"}
              minLength={6}
              required
              autoComplete="new-password"
            />
          </div>

          {status === "error" && message && (
            <Alert variant="destructive" className="text-sm">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          <div className={forced ? "" : "flex gap-2"}>
            {!forced && (
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-10 rounded-lg font-medium text-sm"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={status === "loading"}
              className={forced ? "w-full h-10 rounded-lg font-medium text-sm" : "flex-1 h-10 rounded-lg font-medium text-sm"}
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update password"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
